import { IKey } from '@/common/constants/ikey'
import { isDocumentIdentityFieldKey } from '@/common/utils/documentTransferIdentity'
import { isElementScopedFieldKey } from '@/common/utils/elementScopedUniData'

/** RPC / UI-only suffixes after `{fieldName}__` — не экспортируются в putFullData. */
const PUT_FULL_DATA_AUXILIARY_SUFFIXES = new Set([
  'listbox',
  'items',
  'action',
  'template',
  'page',
  'maxOnPage',
  IKey.LABEL_DATA.replace(/^__/, ''),
  IKey.TT_INIT.replace(/^__/, ''),
  IKey.TT_FORCE_LOAD_ALL.replace(/^__/, ''),
  IKey.TT_SEARCH.replace(/^__/, ''),
  IKey.TT_NEXT_SEARCH_POSITION.replace(/^__/, ''),
  IKey.ROW_CHECKED.replace(/^__/, ''),
  IKey.SHOW_MESSAGE_DIALOG.replace(/^__/, ''),
  IKey.TABLE_SELECTED_ROW.replace(/^__/, ''),
])

const PUT_FULL_DATA_ALLOWED_SUFFIXES = new Set(['text', 'first', 'second', IKey.ROW_NUM])

/**
 * Служебный ключ page/registry для putFullData (Swing getFullData не включает __listbox, __action, …).
 */
export function isPutFullDataAuxiliaryFieldKey(key: string, fieldName: string): boolean {
  const name = String(fieldName ?? '').trim()
  const k = String(key ?? '').trim()
  if (!name || !k) return false
  if (k === name || k.startsWith(`${name}.`)) return false
  if (!k.startsWith(`${name}__`)) return false

  const suffix = k.slice(name.length + 2)
  if (PUT_FULL_DATA_ALLOWED_SUFFIXES.has(suffix)) return false
  if (PUT_FULL_DATA_AUXILIARY_SUFFIXES.has(suffix)) return true
  if (suffix.startsWith('row') && suffix.includes('__')) return true
  return false
}

/** Page snapshot: только ключи элемента без RPC-aux. */
export function pickPutFullDataScopedPageFields(
  pageMerged: Record<string, unknown>,
  fieldName: string,
): Record<string, unknown> {
  const name = String(fieldName ?? '').trim()
  if (!name) return {}

  const out: Record<string, unknown> = {}
  for (const key of Object.keys(pageMerged)) {
    if (key === name || key.startsWith(`${name}.`)) {
      out[key] = pageMerged[key]
      continue
    }
    if (key.startsWith(`${name}__`) && !isPutFullDataAuxiliaryFieldKey(key, name)) {
      out[key] = pageMerged[key]
    }
  }
  return out
}

/** TILES: только таблица (+ опционально rowNum для inline). */
export function pruneTilesPutFullDataFields(
  data: Record<string, unknown>,
  elementName: string,
  options: { includeRowNum?: boolean } = {},
): Record<string, unknown> {
  const name = String(elementName ?? '').trim()
  const out: Record<string, unknown> = {}
  if (name && data[name] !== undefined) {
    out[name] = data[name]
  }
  const rowNumKey = `${name}__${IKey.ROW_NUM}`
  if (options.includeRowNum && data[rowNumKey] !== undefined) {
    out[rowNumKey] = data[rowNumKey]
  }
  return out
}

/**
 * Оставляет только поля, относящиеся к элементу putFullData(name).
 * Для TILES — только таблица (и rowNum при inline).
 */
export function prunePutFullDataCollectedFields(
  collected: Record<string, unknown>,
  fieldName: string,
  elementType?: string,
  options: { includeTilesRowNum?: boolean; extraKeys?: string[] } = {},
): Record<string, unknown> {
  const name = String(fieldName ?? '').trim()
  const elType = String(elementType ?? '').trim().toUpperCase()
  if (!name) return {}

  if (elType === 'TILES') {
    return pruneTilesPutFullDataFields(collected, name, {
      includeRowNum: options.includeTilesRowNum,
    })
  }

  const extra = new Set((options.extraKeys ?? []).map(String))
  const out: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(collected)) {
    if (extra.has(key)) {
      out[key] = value
      continue
    }
    if (key === IKey.COMPLEX_ELEMENT_NAME && elType === 'FILEATTACHMENTS') {
      out[key] = value
      continue
    }
    if (isDocumentIdentityFieldKey(key) && key !== name) continue
    if (!isElementScopedFieldKey(key, name)) continue
    if (isPutFullDataAuxiliaryFieldKey(key, name)) continue
    out[key] = value
  }

  return out
}
